import {
  camelCase,
  cloneDeep,
  difference,
  fromPairs,
  keyBy,
  keys,
  startCase,
  union,
  uniq,
  intersection,
} from 'lodash';
import { AccountInitDataProjectsQuery } from 'api/account';
import { FieldSectionType } from 'api/field-sections/types';
import { CompanyId, ProjectId } from 'api/models';
import { User, Team, MemberType, CompanyType } from 'api/models/core-types';
import { ObjectType } from 'api/pushes';
import { Field as ApiField, StatusFieldOption } from 'api/tasks';
import { ProjectInUserProfile } from 'api/user';
import {
  FieldType,
  FilterableField,
  filterableFieldType,
  SingleSelectField,
  SourceType,
  UsersMetadata,
} from 'components/organisms/tasks/fields';
import { CompaniesStore, Company } from 'stores/companies';
import {
  EditableContent,
  Empty,
  Loaded,
  Loading,
  RefreshableContent,
  Refreshing,
} from 'stores/core';
import { MembershipsStore } from 'stores/memberships';
import { TeamsStore } from 'stores/teams';
import { UsersStore } from 'stores/users';
import { FieldHelper } from 'utils/field';
import { getRandomColor } from 'utils/get-random-color';
import { constructUserMeta } from './util';

type FieldBase = FilterableField &
  // TODO: stop using this
  Pick<
    ApiField,
    | 'fieldDataType'
    | 'fieldType'
    | 'optionsAvailable'
    | 'isEnabled'
    | 'isEditable'
  >;
export type TaskField = FieldBase & { objectType: ObjectType.Task };
export type ProjectField = FieldBase & { objectType: ObjectType.Project };
export type Field = TaskField | ProjectField;

interface ByObjectType {
  ids: number[];
  enabled: number[];
  byFieldName: Record<string, number>;
  byCustomField: {
    true: number[];
    false: number[];
  };
}

export type FieldsStore = {
  ids: RefreshableContent<number[]>;
  byId: Record<number, EditableContent<Field>>;
  originals: {
    byId: Record<number, ApiField>;
    ids: number[];
  };
  byObjectType: {
    [ObjectType.Task]: ByObjectType;
    [ObjectType.Project]: ByObjectType;
  };
};

const EMPTY: FieldsStore = {
  byId: {},
  originals: {
    byId: {},
    ids: [],
  },
  ids: Empty(),
  byObjectType: {
    [ObjectType.Task]: {
      ids: [],
      enabled: [],
      byFieldName: {},
      byCustomField: {
        true: [],
        false: [],
      },
    },
    [ObjectType.Project]: {
      ids: [],
      enabled: [],
      byFieldName: {},
      byCustomField: {
        true: [],
        false: [],
      },
    },
  },
};

export const FIELD_ID_ASSIGNEE = -127;
const FIELD_ID_CREATED_AT = FIELD_ID_ASSIGNEE + 1;
const FIELD_ID_CREATED_BY = FIELD_ID_CREATED_AT + 1;
const FIELD_ID_PROJECT = FIELD_ID_CREATED_BY + 1;
const FIELD_ID_RESPONSIBLE = FIELD_ID_PROJECT + 1;
const FIELD_ID_PRIVATE = FIELD_ID_RESPONSIBLE + 1;

export const FIELD_ID_PROJECT_PHASE_FIELD = FIELD_ID_PRIVATE + 1;
const JIRA_INTEGRATION_FIELD = FIELD_ID_PROJECT_PHASE_FIELD + 1;
const COMPLETED_LATE_BY = JIRA_INTEGRATION_FIELD + 1;

export const FIELD_ID_PROJECT_PHASE_FIELD_WITH_ID = COMPLETED_LATE_BY + 1;
const FIELD_ID_TIME_TRACKED = FIELD_ID_PROJECT_PHASE_FIELD_WITH_ID + 1;
const FIELD_ID_TASK_DEPENDENCY_STATUS = FIELD_ID_TIME_TRACKED + 1;

enum FieldLabel {
  CreatedOn = 'Created on',
  HoursTracked = 'Hours Tracked',
  Responsible = 'Responsible',
  ProjectPhase = 'Project Phase',
  JiraIssue = 'Jira Issue',
  DaysCompletedLateBy = 'Days completed late by',
  TaskDependencyStatus = 'Blocking task status',
}

const CreatedAtField: TaskField = {
  fieldType: 'SINGLE_SELECT',
  fieldDataType: 'DATE',
  type: FieldType.Date,
  customField: false,
  fieldId: FIELD_ID_CREATED_AT,
  fieldName: 'createdAt',
  fieldLabel: FieldLabel.CreatedOn,
  optionsAvailable: false,
  private: false,
  isEnabled: true,
  objectType: ObjectType.Task,
  sourceType: SourceType.Task,
};
const CreatedByField = (users: UsersMetadata): TaskField => ({
  fieldType: 'SINGLE_SELECT',
  fieldDataType: 'USER',
  customField: false,
  type: FieldType.SingleUser,
  fieldId: FIELD_ID_CREATED_BY,
  fieldName: 'createdBy',
  optionsAvailable: false,
  private: false,
  isEnabled: true,
  objectType: ObjectType.Task,
  metaData: {
    users,
  },
  sourceType: SourceType.Task,
});
const TimeTrackedField: TaskField = {
  fieldType: 'SINGLE_SELECT',
  fieldDataType: 'NUMBER',
  customField: false,
  type: FieldType.Number,
  fieldId: FIELD_ID_TIME_TRACKED,
  fieldName: 'timeEntryMinutes',
  fieldLabel: FieldLabel.HoursTracked,
  optionsAvailable: false,
  private: false,
  isEnabled: true,
  objectType: ObjectType.Task,
  sourceType: SourceType.Task,
};

const ProjectField = (
  projects: Pick<ProjectInUserProfile, 'projectName' | 'projectId'>[],
): TaskField => ({
  fieldType: 'SINGLE_SELECT',
  fieldDataType: 'TEXT',
  customField: false,
  type: FieldType.SingleSelect,
  fieldId: FIELD_ID_PROJECT,
  fieldName: 'project',
  optionsAvailable: true,
  metaData: {
    options: projects.map((x) => ({
      label: x.projectName,
      value: x.projectId,
      color: getRandomColor(x.projectId),
      active: true,
    })),
  },
  private: false,
  isEnabled: true,
  objectType: ObjectType.Task,
  sourceType: SourceType.Task,
});
const AssigneeField = (
  users: UsersMetadata,
  teams: Team[],
  projectPlaceholders: { [id: number]: Team[] },
): TaskField => ({
  fieldId: FIELD_ID_ASSIGNEE,
  fieldType: '',
  fieldDataType: '',
  private: false,
  type: FieldType.Assignee,
  customField: false,
  fieldName: 'assignee',
  // @TODO remove the user and teams meta as whole when everthing has been migrated to indexedFb
  metaData: {
    users: {
      active_user_ids: [],
      by_project_id: {},
      by_user_id: {},
      non_external_user_ids: [],
    },
    teams,
    projectPlaceholders,
  },
  // metaData: { users, teams, projectPlaceholders },
  fieldGroup: '_native',
  optionsAvailable: true,
  isEnabled: true,
  objectType: ObjectType.Task,
  sourceType: SourceType.Task,
});
const ResponsibleField = (companies: Company[]): TaskField => ({
  fieldId: FIELD_ID_RESPONSIBLE,
  fieldType: 'MULTI_SELECT',
  fieldDataType: 'TEXT',
  type: FieldType.MultiSelect,
  fieldName: 'responsible',
  fieldLabel: FieldLabel.Responsible,
  isEnabled: true,
  objectType: ObjectType.Task,
  sourceType: SourceType.Task,
  optionsAvailable: true,
  private: false,
  customField: false,
  description: 'Describes who is responsible for this task',
  metaData: {
    options: companies.map((x) => ({
      label: x.companyName,
      value: x.companyId,
      color: getRandomColor(x.companyId),
      active: true,
    })),
  },
});
const PrivateField: TaskField = {
  fieldType: 'SINGLE_SELECT',
  fieldDataType: 'BOOLEAN',
  type: FieldType.Boolean,
  customField: false,
  fieldId: FIELD_ID_PRIVATE,
  fieldName: 'private',
  optionsAvailable: false,
  private: true,
  isEnabled: true,
  objectType: ObjectType.Task,
  sourceType: SourceType.Task,
  userCreated: false,
};

const ProjectPhaseField: TaskField = {
  fieldType: 'TEXT',
  fieldDataType: 'TEXT',
  type: FieldType.Text,
  customField: false,
  fieldId: FIELD_ID_PROJECT_PHASE_FIELD,
  fieldName: 'phaseName',
  fieldLabel: FieldLabel.ProjectPhase,
  optionsAvailable: true,
  private: false,
  isEnabled: true,
  objectType: ObjectType.Task,
  sourceType: SourceType.Task,
  userCreated: false,
};

const ProjectPhaseFieldWithId: TaskField = {
  fieldType: 'SINGLE_SELECT',
  fieldDataType: 'TEXT',
  type: FieldType.SingleSelect,
  customField: false,
  fieldId: FIELD_ID_PROJECT_PHASE_FIELD_WITH_ID,
  fieldName: 'projectPhaseId',
  fieldLabel: FieldLabel.ProjectPhase,
  optionsAvailable: true,
  private: false,
  isEnabled: true,
  objectType: ObjectType.Task,
  sourceType: SourceType.Task,
  userCreated: false,
  metaData: {
    options: [],
  },
};

const JiraIntegrationField: TaskField = {
  fieldType: 'TEXT',
  fieldDataType: 'TEXT',
  type: FieldType.Text,
  customField: false,
  fieldId: JIRA_INTEGRATION_FIELD,
  fieldName: 'jiraIntegration',
  fieldLabel: FieldLabel.JiraIssue,
  optionsAvailable: false,
  private: false,
  isEnabled: true,
  objectType: ObjectType.Task,
  sourceType: SourceType.Task,
  userCreated: false,
};

const CompletedLateBy: TaskField = {
  fieldType: 'NUMBER',
  fieldDataType: 'NUMBER',
  type: FieldType.Number,
  customField: false,
  fieldId: COMPLETED_LATE_BY,
  fieldName: 'completedLateBy',
  fieldLabel: FieldLabel.DaysCompletedLateBy,
  optionsAvailable: false,
  private: false,
  isEnabled: true,
  objectType: ObjectType.Task,
  sourceType: SourceType.Task,
  userCreated: false,
  metaData: {
    required: true,
    rangeAvailable: false,
  },
};

const taskDependencyStatusField = (options): TaskField => ({
  fieldType: 'SINGLE_SELECT',
  fieldDataType: 'TEXT',
  type: FieldType.SingleSelect,
  customField: false,
  fieldId: FIELD_ID_TASK_DEPENDENCY_STATUS,
  fieldName: 'taskDependencyStatus',
  fieldLabel: FieldLabel.TaskDependencyStatus,
  optionsAvailable: true,
  private: false,
  isEnabled: true,
  objectType: ObjectType.Task,
  sourceType: SourceType.Task,
  userCreated: false,
  metaData: {
    options,
    required: true,
  },
});

const constructByObjectType = (fields: Field[]): ByObjectType => {
  const fields_lookup = keyBy(fields, 'fieldId');
  const ids = keys(fields_lookup).map(Number);

  return {
    byCustomField: {
      true: ids.filter((x) => !!fields_lookup[x].customField),
      false: ids.filter((x) => !fields_lookup[x].customField),
    },
    byFieldName: fromPairs(
      fields.map(({ fieldId, fieldName }) => [fieldName, fieldId]),
    ),
    enabled: ids.filter((x) => !!fields_lookup[x].isEnabled),
    ids,
  };
};

const load =
  () =>
  ({ byId, ids, originals, byObjectType }: FieldsStore): FieldsStore => ({
    byId,
    ids:
      ids[0] === 'refreshing' || ids[0] === 'loading'
        ? ids
        : ids[0] === 'loaded'
        ? Refreshing(ids)
        : Loading(),
    originals,
    byObjectType,
  });

const putAll =
  (originals: ApiField[]) =>
  (store: FieldsStore): FieldsStore => {
    const fields = originals.map((field) =>
      field.fieldName === 'defaultTimeEntryCategory'
        ? {
            ...field,
            metaData: {
              ...field?.metaData,
              options: (field?.metaData?.options ?? []).map((option) => ({
                ...option,
                color: getRandomColor(option?.value),
              })),
            },
          }
        : field,
    );

    return {
      ...store,
      originals: {
        byId: keyBy(fields, 'fieldId'),
        ids: fields.map(({ fieldId }) => fieldId),
      },
    };
  };

const add =
  (field: ApiField) =>
  ({ originals: { byId, ids }, ...store }: FieldsStore): FieldsStore => ({
    ...store,
    originals: {
      byId: { ...byId, [field.fieldId]: field },
      ids: union(ids, [field.fieldId]),
    },
  });

const remove =
  (fieldId: number) =>
  ({
    originals: {
      byId: { [fieldId]: deleted, ...rest },
      ids,
    },
    ...store
  }: FieldsStore): FieldsStore => ({
    ...store,
    originals: {
      byId: rest,
      ids: difference(ids, [fieldId]),
    },
  });

const refresh =
  (
    usersStore: UsersStore,
    teamsStore: TeamsStore,
    membershipsStore: MembershipsStore,
    companiesStore: CompaniesStore,
    [, currentCompanyId]: CompanyId,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    currentUser: User,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    systemBotId?: number,
    projectsData?: AccountInitDataProjectsQuery,
  ) =>
  (store: FieldsStore): FieldsStore => {
    if (
      !TeamsStore.loaded(teamsStore) ||
      !UsersStore.loaded(usersStore) ||
      !CompaniesStore.loaded(companiesStore)
    ) {
      return store;
    }
    const users_list = UsersStore.toList(usersStore);
    const active_users = UsersStore.activeUsers(usersStore);
    const active_user_ids = active_users.map(({ userId }) => userId);
    const non_external_user_ids = active_users.map(({ userId }) => userId);
    const native_user_ids = UsersStore.ofMemberType(MemberType.Native)(
      usersStore,
    );
    const private_user_ids = UsersStore.ofCompanyId(
      CompanyId(currentCompanyId),
    )(usersStore);
    const private_active_user_ids = intersection(
      private_user_ids,
      active_user_ids,
    );
    const teams = TeamsStore.toList(teamsStore);
    const projectsArr = projectsData?.projects ?? [];
    const projectIds = projectsArr.map(({ projectId }) => ProjectId(projectId));
    const projectPlaceholders: { [id: number]: Team[] } = {};

    projectIds.forEach((projectId) => {
      const memberships =
        MembershipsStore.ofProject(projectId)(membershipsStore);
      const project_placeholders = memberships[1]?.placeholders ?? [];

      projectPlaceholders[projectId[1]] = project_placeholders;
    });
    const users = constructUserMeta({
      membershipsStore,
      usersStore,
      current_company_user_ids: private_user_ids,
      native_user_ids,
      active_user_ids,
      non_external_user_ids,
      current_company_active_user_ids: private_active_user_ids,
      projectIds,
      users_list,
      currentUser,
    });
    const companies = union(
      uniq(projectsArr.map((x) => x.customer.companyId).filter((x) => x >= 0)),
      [currentCompanyId],
    )
      .map(CompanyId)
      .map((x) => CompaniesStore.ofId(x)(companiesStore))
      .concat(
        CompaniesStore.toList(companiesStore).filter(
          (x) => x.companyType === CompanyType.Partner,
        ),
      )
      .filter(Boolean);
    const [task_fields, project_fields] = [
      FieldSectionType.Task,
      FieldSectionType.Project,
    ].map((type) => {
      return store.originals.ids
        .map((x) => store.originals.byId[x])
        .filter(({ objectType }) => objectType === type)
        .map((x) => ({
          ...x,
          type: filterableFieldType(x),
          sourceType:
            type === FieldSectionType.Task
              ? SourceType.Task
              : SourceType.Project,
        }));
    }) as unknown as [TaskField[], ProjectField[]];

    // @TODO remove this after testing indexed db implementation
    // commented bacauset the loader is not hiding since members store is empty
    const createdByUsers = {
      active_user_ids: [],
      by_project_id: {},
      by_user_id: {},
      non_external_user_ids: [],
    };

    const statusFieldOptions = (
      task_fields.find(
        (field) => field.fieldName === 'Status',
      ) as SingleSelectField
    )?.metaData.options;

    const fields_with_metadata = task_fields
      .map((f) => ({ ...f, fieldName: camelCase(f.fieldName) }))
      .concat(
        AssigneeField(users, teams, projectPlaceholders),
        CreatedAtField,
        CreatedByField(createdByUsers),
        TimeTrackedField,
        ProjectField(projectsArr),
        ResponsibleField(companies),
        PrivateField,
        JiraIntegrationField,
        CompletedLateBy,
        taskDependencyStatusField(statusFieldOptions),
        ProjectPhaseField,
        ProjectPhaseFieldWithId,
      )
      .map(
        (f) =>
          ({
            ...f,
            fieldLabel: (f as any).fieldLabel ?? startCase(f.fieldName),
            metaData: {
              ...f.metaData,
              users:
                f.fieldDataType === 'USER' && f.fieldName !== 'createdBy'
                  ? users
                  : (f.metaData as any)?.users,
            },
          } as TaskField),
      );

    const transformed_fields = [...fields_with_metadata, ...project_fields].map(
      (field): Field => {
        switch (field.type) {
          case FieldType.SingleSelect:
          case FieldType.MultiSelect:
            return {
              ...field,
              metaData: {
                ...field.metaData,
                options: FieldHelper(field).options.allOptions,
              },
            };
          case FieldType.Assignee:
          case FieldType.Boolean:
          case FieldType.Date:
          case FieldType.Estimate:
          case FieldType.MultiUser:
          case FieldType.SingleUser:
          case FieldType.Number:
          case FieldType.Text:
          default:
            return field;
        }
      },
    );

    const fields_lookup = keyBy(transformed_fields, 'fieldId');
    const ids = keys(fields_lookup).map(Number);

    return {
      byId: fromPairs(ids.map((x) => [x, Loaded(fields_lookup[x])])),
      originals: store.originals,
      ids: Loaded(ids),
      byObjectType: {
        [ObjectType.Task]: constructByObjectType(fields_with_metadata),
        [ObjectType.Project]: constructByObjectType(project_fields),
      },
    };
  };

const loaded = ({ ids: [status] }: FieldsStore) =>
  status === 'loaded' || status === 'refreshing';
const refreshing = ({ ids: [status] }: FieldsStore) => status === 'refreshing';

const toList = ({ ids: [, fields = []], byId }: FieldsStore) =>
  fields.map((x) => byId[x]).filter(Boolean);

const enabledTaskFields = ({
  byId,
  byObjectType: {
    [ObjectType.Task]: { enabled },
  },
}: FieldsStore) =>
  enabled
    .map((x) => byId[x])
    .filter(Boolean)
    .map(([, x]) => x)
    .filter(Boolean)
    .map((x) => {
      if (x?.fieldName === 'projectPhaseId') {
        return cloneDeep(x) as TaskField;
      }

      return { ...x } as TaskField;
    });

const allTaskFields = ({
  byId,
  byObjectType: {
    [ObjectType.Task]: { enabled },
  },
}: FieldsStore) =>
  enabled
    .map((x) => byId[x])
    .filter(Boolean)
    .map(([, x]) => x)
    .filter(Boolean)
    .map((x) => cloneDeep(x) as TaskField);

const getTaskFieldByName =
  (fieldName: string) =>
  ({
    byId,
    byObjectType: {
      [ObjectType.Task]: { byFieldName },
    },
  }: FieldsStore): EditableContent<TaskField> =>
    (byId[byFieldName[fieldName]] as any) ?? Empty();

export type StatusField = TaskField &
  SingleSelectField & {
    metaData: SingleSelectField['metaData'] & {
      options: StatusFieldOption[];
      default: number;
    };
  };

const getTaskStatusField = (store: FieldsStore): EditableContent<StatusField> =>
  getTaskFieldByName('status')(store) as EditableContent<StatusField>;

const getProjectStatusFieldId = (store: FieldsStore): number =>
  store.byObjectType.PROJECT.byFieldName.Status;

export const FieldsStore = {
  empty: () => EMPTY,
  load,
  putAll,
  loaded,
  toList,
  enabledTaskFields,
  allTaskFields,
  add,
  remove,
  refresh,
  refreshing,
  getTaskFieldByName,
  getTaskStatusField,
  getProjectStatusFieldId,
};
